import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import PaymentModal from '../components/PaymentModal';
import PaymentReceiptModal from '../components/PaymentReceiptModal';
import { usePayments, usePaymentStatistics } from '../hooks/usePayments';
import { useInvoices } from '../hooks/useInvoices';

const { 
  FiPlus, FiSearch, FiEdit2, FiTrash2, FiEye, FiDownload, FiSend,
  FiRefreshCw, FiDollarSign, FiCalendar, FiCreditCard, FiAlertTriangle,
  FiCheckCircle, FiClock, FiTrendingUp, FiGrid, FiList, FiArrowUp, FiArrowDown,
  FiMail, FiFileText
} = FiIcons;

const Payments = () => {
  const { payments, loading, error, recordPayment, updatePayment, deletePayment, refetch } = usePayments();
  const { stats, loading: statsLoading } = usePaymentStatistics();
  const { invoices } = useInvoices();
  const [showModal, setShowModal] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBy, setFilterBy] = useState('all');
  const [viewMode, setViewMode] = useState('list');
  const [sortBy, setSortBy] = useState('payment_date');
  const [sortOrder, setSortOrder] = useState('desc');

  // Filter and search payments
  const filteredPayments = payments
    .filter(payment => {
      const matchesSearch = 
        payment.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        payment.client?.business_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        payment.client?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        payment.transaction_id?.toLowerCase().includes(searchTerm.toLowerCase());
      
      if (filterBy === 'all') return matchesSearch;
      if (filterBy === 'paid') return matchesSearch && payment.payment_status === 'paid';
      if (filterBy === 'partial') return matchesSearch && payment.payment_status === 'partial';
      if (filterBy === 'pending') return matchesSearch && payment.payment_status === 'pending';
      if (filterBy === 'overdue') return matchesSearch && payment.payment_status === 'overdue';
      if (filterBy === 'late_fees') return matchesSearch && payment.late_fee > 0;
      
      return matchesSearch;
    })
    .sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'invoice_number':
          aValue = a.invoice_number || '';
          bValue = b.invoice_number || '';
          break;
        case 'client':
          aValue = a.client?.business_name || '';
          bValue = b.client?.business_name || '';
          break;
        case 'paid_amount':
          aValue = a.paid_amount || 0;
          bValue = b.paid_amount || 0;
          break;
        case 'payment_date':
          aValue = new Date(a.payment_date || 0);
          bValue = new Date(b.payment_date || 0);
          break;
        default:
          aValue = new Date(a.payment_date || 0);
          bValue = new Date(b.payment_date || 0);
      }
      
      if (typeof aValue === 'string') {
        return sortOrder === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
    });

  const handleViewReceipt = (payment) => {
    setSelectedPayment(payment);
    setShowReceiptModal(true);
  };

  const handleEditPayment = (payment) => {
    setSelectedPayment(payment);
    setShowModal(true);
  };

  const handleDeletePayment = async (id) => {
    if (window.confirm('Are you sure you want to delete this payment record? This action cannot be undone.')) {
      const result = await deletePayment(id);
      if (result.success) {
        // Success feedback could be added here
      }
    }
  };

  const handleRecordPayment = async (paymentData) => {
    const result = await recordPayment(paymentData);
    if (result.success) {
      setShowModal(false);
      setSelectedPayment(null);
    }
    return result;
  };

  const handleUpdatePayment = async (paymentData) => {
    if (selectedPayment) {
      const result = await updatePayment(selectedPayment.id, paymentData);
      if (result.success) {
        setShowModal(false);
        setSelectedPayment(null);
      }
      return result;
    }
  };

  const formatCurrency = (amount) => {
    return `$${parseFloat(amount || 0).toLocaleString()}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-800 border-green-200';
      case 'partial': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'pending': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'overdue': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getPaymentMethodIcon = (method) => {
    switch (method) {
      case 'credit_card': return FiCreditCard;
      case 'bank_transfer': return FiCreditCard;
      case 'paypal': return FiDollarSign;
      case 'stripe': return FiCreditCard;
      case 'wire_transfer': return FiSend;
      case 'check': return FiFileText;
      case 'cash': return FiDollarSign;
      default: return FiDollarSign;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <SafeIcon icon={FiRefreshCw} className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Loading payments...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="flex flex-col lg:flex-row lg:items-center lg:justify-between"
      >
        <div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Payment Tracking</h1>
          <p className="text-gray-600">
            Track payment status, manage late fees, and generate receipts
          </p>
        </div>
        
        <div className="flex items-center space-x-3 mt-4 lg:mt-0">
          <button
            onClick={refetch}
            className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200"
          >
            <SafeIcon icon={FiRefreshCw} className="w-4 h-4" />
            <span>Refresh</span>
          </button>
          
          <button
            onClick={() => {
              setSelectedPayment(null);
              setShowModal(true);
            }}
            className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors duration-200"
          >
            <SafeIcon icon={FiPlus} className="w-4 h-4" />
            <span>Record Payment</span>
          </button>
        </div>
      </motion.div>

      {/* Summary Cards */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
      >
        <div className="bg-white rounded-xl p-6 shadow-soft border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Received</p>
              <p className="text-2xl font-bold text-green-600">
                {formatCurrency(stats.total_amount_received || 0)}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiCheckCircle} className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-soft border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Outstanding Balance</p>
              <p className="text-2xl font-bold text-orange-600">
                {formatCurrency(stats.outstanding_balance || 0)}
              </p>
            </div>
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiClock} className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-soft border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Overdue Payments</p>
              <p className="text-2xl font-bold text-red-600">{stats.total_overdue || 0}</p>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiAlertTriangle} className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-soft border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Late Fees</p>
              <p className="text-2xl font-bold text-purple-600">
                {formatCurrency(stats.total_late_fees || 0)}
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiTrendingUp} className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>
      </motion.div>

      {/* Search, Filter, and Controls */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="bg-white rounded-xl p-6 shadow-soft border border-gray-200"
      >
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
            <div className="relative flex-1 min-w-80">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search payments by invoice, client, or transaction ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
            
            <select
              value={filterBy}
              onChange={(e) => setFilterBy(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="all">All Payments ({payments.length})</option>
              <option value="paid">Fully Paid</option>
              <option value="partial">Partial Payments</option>
              <option value="pending">Pending</option>
              <option value="overdue">Overdue</option>
              <option value="late_fees">With Late Fees</option>
            </select>
            
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="payment_date">Sort by Payment Date</option>
              <option value="invoice_number">Sort by Invoice #</option>
              <option value="client">Sort by Client</option>
              <option value="paid_amount">Sort by Amount</option>
            </select>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200"
              title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
            >
              <SafeIcon icon={sortOrder === 'asc' ? FiArrowUp : FiArrowDown} className="w-4 h-4" />
            </button>
            
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg transition-colors duration-200 ${
                viewMode === 'grid' ? 'bg-green-100 text-green-600 border border-green-200' : 'hover:bg-gray-100 border border-gray-300'
              }`}
            >
              <SafeIcon icon={FiGrid} className="w-4 h-4" />
            </button>
            
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-lg transition-colors duration-200 ${
                viewMode === 'list' ? 'bg-green-100 text-green-600 border border-green-200' : 'hover:bg-gray-100 border border-gray-300'
              }`}
            >
              <SafeIcon icon={FiList} className="w-4 h-4" />
            </button>
          </div>
        </div>
        
        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-gray-600">
            Showing {filteredPayments.length} of {payments.length} payments
          </p>
          
          {error && (
            <div className="text-red-600 text-sm">
              {error} (Running in demo mode)
            </div>
          )}
        </div>
      </motion.div>

      {/* Payments Display */}
      {filteredPayments.length > 0 ? (
        <div className={viewMode === 'grid' ? 
          'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6' : 
          'space-y-4'
        }>
          {filteredPayments.map((payment, index) => (
            <motion.div
              key={payment.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.05 }}
              className={`bg-white rounded-xl p-6 shadow-soft hover:shadow-medium transition-all duration-300 border border-gray-200 ${
                viewMode === 'list' ? 'flex items-center justify-between' : ''
              }`}
            >
              <div className={`${viewMode === 'list' ? 'flex-1' : ''}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-800 mb-1">{payment.invoice_number}</h3>
                    <p className="text-gray-600 mb-2">{payment.client?.business_name}</p>
                    <div className="flex items-center space-x-2 flex-wrap gap-1">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(payment.payment_status)}`}>
                        {payment.payment_status.charAt(0).toUpperCase() + payment.payment_status.slice(1)}
                      </span>
                      {payment.late_fee > 0 && (
                        <span className="px-3 py-1 rounded-full text-xs font-medium border bg-red-100 text-red-800 border-red-200">
                          Late Fee: {formatCurrency(payment.late_fee)}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {viewMode === 'grid' && (
                    <div className="flex items-center space-x-1 ml-2">
                      <button 
                        onClick={() => handleViewReceipt(payment)}
                        className="p-2 hover:bg-blue-50 rounded-lg transition-colors duration-200"
                        title="View Receipt"
                      >
                        <SafeIcon icon={FiEye} className="w-4 h-4 text-blue-600" />
                      </button>
                      <button 
                        onClick={() => handleEditPayment(payment)}
                        className="p-2 hover:bg-green-50 rounded-lg transition-colors duration-200"
                        title="Edit Payment"
                      >
                        <SafeIcon icon={FiEdit2} className="w-4 h-4 text-green-600" />
                      </button>
                      <button 
                        onClick={() => handleDeletePayment(payment.id)}
                        className="p-2 hover:bg-red-50 rounded-lg transition-colors duration-200"
                        title="Delete Payment"
                      >
                        <SafeIcon icon={FiTrash2} className="w-4 h-4 text-red-600" />
                      </button>
                    </div>
                  )}
                </div>

                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex items-center space-x-3">
                    <SafeIcon icon={FiDollarSign} className="w-4 h-4 text-gray-400" />
                    <span>Paid: <strong>{formatCurrency(payment.paid_amount)}</strong></span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <SafeIcon icon={FiCalendar} className="w-4 h-4 text-gray-400" />
                    <span>Date: {formatDate(payment.payment_date)}</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <SafeIcon icon={getPaymentMethodIcon(payment.payment_method)} className="w-4 h-4 text-gray-400" />
                    <span>Method: {payment.payment_method?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                  </div>
                  {payment.remaining_balance > 0 && (
                    <div className="flex items-center space-x-3">
                      <SafeIcon icon={FiAlertTriangle} className="w-4 h-4 text-orange-400" />
                      <span className="text-orange-600">Balance: <strong>{formatCurrency(payment.remaining_balance)}</strong></span>
                    </div>
                  )}
                </div>

                {viewMode === 'grid' && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="flex items-center justify-between">
                      <div className="text-xs text-gray-500">
                        {payment.transaction_id ? `TXN: ${payment.transaction_id}` : 'No transaction ID'}
                      </div>
                      
                      <div className="flex items-center space-x-1">
                        <button
                          className="p-1 hover:bg-gray-100 rounded transition-colors duration-200"
                          title="Download Receipt"
                        >
                          <SafeIcon icon={FiDownload} className="w-3 h-3 text-gray-600" />
                        </button>
                        <button
                          className="p-1 hover:bg-gray-100 rounded transition-colors duration-200"
                          title="Send Receipt"
                        >
                          <SafeIcon icon={FiSend} className="w-3 h-3 text-gray-600" />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {viewMode === 'list' && (
                <div className="flex items-center space-x-4 ml-6">
                  <div className="text-right min-w-32">
                    <p className="text-lg font-bold text-gray-800">{formatCurrency(payment.paid_amount)}</p>
                    <p className="text-xs text-gray-500">{formatDate(payment.payment_date)}</p>
                    {payment.remaining_balance > 0 && (
                      <p className="text-xs text-orange-600">Balance: {formatCurrency(payment.remaining_balance)}</p>
                    )}
                  </div>
                  <div className="flex items-center space-x-1">
                    <button 
                      onClick={() => handleViewReceipt(payment)}
                      className="p-2 hover:bg-blue-50 rounded-lg transition-colors duration-200"
                      title="View Receipt"
                    >
                      <SafeIcon icon={FiEye} className="w-4 h-4 text-blue-600" />
                    </button>
                    <button 
                      onClick={() => handleEditPayment(payment)}
                      className="p-2 hover:bg-green-50 rounded-lg transition-colors duration-200"
                      title="Edit Payment"
                    >
                      <SafeIcon icon={FiEdit2} className="w-4 h-4 text-green-600" />
                    </button>
                    <button 
                      onClick={() => handleDeletePayment(payment.id)}
                      className="p-2 hover:bg-red-50 rounded-lg transition-colors duration-200"
                      title="Delete Payment"
                    >
                      <SafeIcon icon={FiTrash2} className="w-4 h-4 text-red-600" />
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl p-12 shadow-soft text-center border border-gray-200"
        >
          <SafeIcon icon={FiDollarSign} className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-800 mb-2">
            {searchTerm || filterBy !== 'all' ? 'No payments found' : 'No payments recorded yet'}
          </h3>
          <p className="text-gray-600 mb-6">
            {searchTerm || filterBy !== 'all' 
              ? 'Try adjusting your search or filter criteria'
              : 'Start by recording your first payment to track payment status'
            }
          </p>
          {(!searchTerm && filterBy === 'all') && (
            <button
              onClick={() => {
                setSelectedPayment(null);
                setShowModal(true);
              }}
              className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg transition-colors duration-200"
            >
              Record Your First Payment
            </button>
          )}
        </motion.div>
      )}

      {/* Modals */}
      <PaymentModal 
        isOpen={showModal} 
        onClose={() => {
          setShowModal(false);
          setSelectedPayment(null);
        }}
        payment={selectedPayment}
        invoices={invoices}
        onSubmit={selectedPayment ? handleUpdatePayment : handleRecordPayment}
      />

      <PaymentReceiptModal
        isOpen={showReceiptModal}
        onClose={() => {
          setShowReceiptModal(false);
          setSelectedPayment(null);
        }}
        payment={selectedPayment}
      />
    </div>
  );
};

export default Payments;